import { z } from "zod";
import { type Subject, teacherSubjects } from "./subjects.ts";
import { UserId } from "./user.ts";

/**
 * Карточки: колоды, выдачи, повторения. Проект целиком в
 * `docs/flashcards-design.md`, здесь контракты поставки 1 (раздел 9.1).
 *
 * ⚠️ Файл лежит в КОРНЕВОМ экспорте, то есть едет в стартовый чанк всем. Поэтому
 * здесь только схемы, потолки и одна функция видимости поля; `ts-fsrs`, свёртка
 * журнала и очередь на сегодня живут за подпутём `@edu/shared/flashcards`
 * (`./flashcards-schedule.ts`) и грузятся только ленивым чанком тренажёра.
 * Модуль досылки очереди (7.4) зовёт стартовый чанк и читает отсюда, а не оттуда.
 *
 * ⚠️ Ни одного импорта из `./checking.ts`, `./checking-answer.ts` и
 * `./exercises*.ts`. Эталона нерешённого задания у тренажёра нет: обе стороны
 * карточки уезжают ученику целиком (6), а вот подпути с ответами в стартовый
 * чанк ехать нельзя. Отсюда и копия регулярки `AttemptId` ниже.
 *
 * Здесь данные и ручки преподавателя. Соседи в том же корневом экспорте, разрез
 * по читателю: `./flashcards-student.ts` (колода получателя, `today`,
 * повторения, сводка), `./flashcards-words.ts` (поле «Слова занятия»),
 * `./flashcards-paste.ts` (разбор вставки, `frontKey`, дописывание оборота).
 */

/* ──────────────────────────── потолки ──────────────────────────── */

/** Сторона карточки, как `ExerciseToken.text` (3.2). */
export const FLASHCARD_SIDE_MAX = 300;
/** Пример употребления у карточки (3.2). */
export const FLASHCARD_EXAMPLE_MAX = 500;
/** Название колоды (3.1). */
export const FLASHCARD_DECK_TITLE_MAX = 200;
/**
 * Живых карточек в колоде (3.1). У обычной колоды сверх него `deck_full`; колода
 * слов не отказывает, а заводит следующую часть, и часть может перешагнуть
 * потолок на одну пачку.
 */
export const FLASHCARD_DECK_MAX = 2000;
/** Пачка `POST /v1/decks/:deckId/cards` (9.1). */
export const FLASHCARD_CARDS_BATCH_MAX = 500;
/** Пачка `POST /v1/flashcards/reviews` (9.1). */
export const FLASHCARD_REVIEWS_BATCH_MAX = 200;
/** Новых в день на ученика, по всем колодам и всем преподавателям (4.3, 15.9). */
export const FLASHCARD_NEW_PER_DAY_TOTAL = 20;
/** Показов в одной сессии; повтор карточки после «Снова» это ещё один показ. */
export const FLASHCARD_SESSION_MAX = 60;
/** Новые колоды не вводятся, пока просроченных в ней больше этого. */
export const FLASHCARD_OVERDUE_GATE = 30;
/** `new_per_day` выдачи обычной колоды (3.3); у колоды слов он `null`. */
export const FLASHCARD_NEW_PER_DAY_MIN = 1;
export const FLASHCARD_NEW_PER_DAY_MAX = 50;
/**
 * `new_per_day` обычной колоды, когда «Открыть ученику» его не прислало.
 * Документ числа не задаёт; половина общего потолка, чтобы одна колода не
 * съедала весь день.
 */
export const FLASHCARD_NEW_PER_DAY_DEFAULT = 10;
/** Корзина колод и карточек до уборки сторожем (9.2). */
export const FLASHCARD_TRASH_DAYS = 30;
/** `browsed_at` пишется не чаще (5.2); держит сервер условием в `UPDATE`. */
export const FLASHCARD_BROWSED_THROTTLE_HOURS = 12;
/** Окно `dayStart` у `today`: от «сейчас минус 36 часов» до «сейчас» (4.3). */
export const FLASHCARD_DAY_START_WINDOW_MS = 36 * 3_600_000;
/**
 * Сессия кончается, когда остались только карточки на коротком шаге, ждать
 * которых дольше этого (4.3).
 */
export const FLASHCARD_SHORT_STEP_WAIT_MS = 60_000;

/* ──────────────────────────── идентификаторы ───────────────────── */

export const FlashcardDeckId = z.string().uuid();
export type FlashcardDeckId = z.infer<typeof FlashcardDeckId>;
export const FlashcardCardId = z.string().uuid();
export type FlashcardCardId = z.infer<typeof FlashcardCardId>;

/**
 * Клиентский id повторения (3.3, 7.3): ключ идемпотентности досылки.
 *
 * ⚠️ Регулярка СКОПИРОВАНА с `AttemptId` (`./checking.ts`), а не импортирована:
 * импорт значением из `./checking` вытащил бы подпуть проверки в стартовый
 * чанк. Довод за шестнадцатеричную форму тот же: в незащищённом контексте
 * `crypto.randomUUID` нет, и id собирается из `getRandomValues` без дефисов.
 */
export const FlashcardReviewId = z
  .string()
  .regex(/^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i);
export type FlashcardReviewId = z.infer<typeof FlashcardReviewId>;

/* ──────────────────────────── данные ───────────────────────────── */

/** Откуда карточка (3.2). Ученику не отдаётся ни в одном ответе. */
export const FlashcardOrigin = z.enum(["manual", "paste", "occurrence", "student", "chat", "draft"]);
export type FlashcardOrigin = z.infer<typeof FlashcardOrigin>;

/** Оценка FSRS: 1 «Снова», 2 «Трудно», 3 «Помню», 4 «Легко». Поставка 1 шлёт 1 и 3. */
export const FlashcardRating = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export type FlashcardRating = z.infer<typeof FlashcardRating>;

/** Как получена оценка: самооценка переворотом или проверенный ввод (поставка 2). */
export const FlashcardReviewMode = z.enum(["flip", "input"]);
export type FlashcardReviewMode = z.infer<typeof FlashcardReviewMode>;

/**
 * Состояние карточки в графике повторений: свёртка журнала (4.2), а не
 * хранимая строка. Поля зеркалят `Card` из `ts-fsrs`, времена в миллисекундах
 * эпохи; перевод туда и обратно живёт в `./flashcards-schedule.ts`.
 *
 * `elapsed_days` в проводе нет: в `ts-fsrs` 5 он помечен устаревшим, интервал
 * считается от `last_review`, и при переводе обратно поле восстанавливается нулём.
 *
 * ⚠️ Состояние ПОКАЗЫВАЕТСЯ ученику целиком, и это не утечка: это его же
 * повторения, а клиенту оно нужно, чтобы положить неотправленную очередь поверх.
 */
export const FlashcardCardState = z.object({
  state: z.enum(["new", "learning", "review", "relearning"]),
  due: z.number().int(),
  stability: z.number(),
  difficulty: z.number(),
  scheduledDays: z.number(),
  learningSteps: z.number().int(),
  reps: z.number().int(),
  lapses: z.number().int(),
  lastReview: z.number().int().nullable(),
});
export type FlashcardCardState = z.infer<typeof FlashcardCardState>;

/** Колода слов пары: чья и какая часть (3.1). `null` у обычной колоды и у осиротевшей. */
export const FlashcardWordsDeck = z.object({
  studentUserId: UserId,
  part: z.number().int().min(1),
});
export type FlashcardWordsDeck = z.infer<typeof FlashcardWordsDeck>;

/** Получатель колоды в глазах владельца: строка выдачи плюс имя. */
export const FlashcardRecipient = z.object({
  userId: UserId,
  name: z.string(),
  newPerDay: z.number().int().nullable(),
  createdAt: z.number().int(),
  revokedAt: z.number().int().nullable(),
  browsedAt: z.number().int().nullable(),
});
export type FlashcardRecipient = z.infer<typeof FlashcardRecipient>;

/**
 * Строка списка колод преподавателя (`GET /v1/decks`). Пустой `recipients` это
 * «никому не открыт», в том числе у колоды слов удалённой пары (3.1).
 */
export const FlashcardDeckSummary = z.object({
  id: FlashcardDeckId,
  title: z.string(),
  rev: z.number().int(),
  cardCount: z.number().int(),
  words: FlashcardWordsDeck.nullable(),
  recipients: z.array(FlashcardRecipient),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type FlashcardDeckSummary = z.infer<typeof FlashcardDeckSummary>;

/** Карточка в глазах владельца. */
export const FlashcardOwnerCard = z.object({
  id: FlashcardCardId,
  position: z.number().int(),
  front: z.string(),
  back: z.string(),
  example: z.string().nullable(),
  origin: FlashcardOrigin,
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type FlashcardOwnerCard = z.infer<typeof FlashcardOwnerCard>;

/* ──────────────────────────── запросы колоды ───────────────────── */

const DeckTitle = z.string().trim().min(1).max(FLASHCARD_DECK_TITLE_MAX);

/** `POST /v1/decks`; `requestId` уникален с владельцем (3.1). Ответ `FlashcardDeckSummary`. */
export const CreateDeckRequest = z.object({
  requestId: z.string().uuid(),
  title: DeckTitle,
});
export type CreateDeckRequest = z.infer<typeof CreateDeckRequest>;

/** `PATCH /v1/decks/:deckId`. Ответ `FlashcardDeckSummary`. */
export const UpdateDeckRequest = z.object({ title: DeckTitle });
export type UpdateDeckRequest = z.infer<typeof UpdateDeckRequest>;

/** `GET /v1/decks`. */
export const DeckListResponse = z.object({ decks: z.array(FlashcardDeckSummary) });
export type DeckListResponse = z.infer<typeof DeckListResponse>;

/**
 * Сторона карточки. Перевод строки и Tab запрещены: в разборе вставки это
 * разделители, и сторона с ними не пережила бы экспорт и повторную вставку.
 */
export const FlashcardSide = z
  .string()
  .trim()
  .min(1)
  .max(FLASHCARD_SIDE_MAX)
  .refine((v) => !/[\r\n\t]/u.test(v), "без перевода строки и Tab");

/** Пример употребления; пустая строка после обрезки это `null`, а не «пример пуст». */
export const FlashcardExample = z
  .string()
  .trim()
  .max(FLASHCARD_EXAMPLE_MAX)
  .transform((v) => (v === "" ? null : v));

/**
 * Разделитель внутри строки, выбранный человеком в ответ на вопрос (10.3):
 * «как скопировано из Quizlet» это `tab`, дефис это `dash`, двоеточие и свой
 * знак это `text`. `dash` режет по первому тире в окружении пробелов (любое из
 * трёх), а если такого в строке нет, по первому дефису или тире вообще:
 * человек, ответивший «дефис», пишет и «run-бежать».
 */
export const CardSeparator = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("tab") }),
  z.object({ kind: z.literal("dash") }),
  z.object({ kind: z.literal("text"), text: z.string().min(1).max(8) }),
]);
export type CardSeparator = z.infer<typeof CardSeparator>;

/** Текст вставки: строка на карточку (8.1). Пустой после обрезки это `deck_paste_empty`. */
/**
 * Потолок длины: строк не больше пачки, в строке две стороны, пример (третья
 * колонка Tab) и два разделителя до 8 знаков (`CardSeparator`), плюс перевод
 * строки. Пустые строки сюда не заложены: это потолок на вес тела, а не счёт.
 */
export const PasteText = z
  .string()
  .max(FLASHCARD_CARDS_BATCH_MAX * (FLASHCARD_SIDE_MAX * 2 + FLASHCARD_EXAMPLE_MAX + 2 * 8 + 2));

/**
 * `POST /v1/decks/:deckId/cards`: вставка текстом или одна карточка из полей.
 * Сервер разбирает вставку той же `parseCardPaste`, что показала предпросмотр,
 * поэтому разделитель приезжает только тогда, когда человека спросили.
 * Непустых строк не больше `FLASHCARD_CARDS_BATCH_MAX`: сверх `parseCardPaste`
 * отвечает `tooMany`, и сервер отказывает `invalid_body`.
 *
 * Пример у карточки из полей ложится на СУЩЕСТВУЮЩУЮ карточку с тем же ключом,
 * только если её пример пуст; иначе он не пишется, и отчёт называет её в
 * `exampleSkipped`.
 */
export const AddCardsRequest = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("paste"), text: PasteText, separator: CardSeparator.optional() }),
  z.object({
    kind: z.literal("card"),
    front: FlashcardSide,
    back: FlashcardSide,
    example: FlashcardExample.optional(),
  }),
]);
export type AddCardsRequest = z.infer<typeof AddCardsRequest>;

/**
 * Итог вставки СЛОВАМИ, а не числами (10.3): «уже есть: run, jump», «дополнено:
 * run», «не поместилось: run». Во всех списках слов лицевые стороны; номера
 * строк у неразобранных и слишком длинных, считая с единицы.
 */
export const PasteReport = z.object({
  added: z.array(z.string()),
  extended: z.array(z.string()),
  existing: z.array(z.string()),
  overflow: z.array(z.string()),
  exampleSkipped: z.array(z.string()),
  unparsed: z.array(z.number().int().min(1)),
  tooLong: z.array(z.number().int().min(1)),
});
export type PasteReport = z.infer<typeof PasteReport>;

/**
 * Ответ вставки. `question` значит «разделитель не угадан, ничего не
 * сохранено»: клиент задаёт вопрос 10.3 и повторяет запрос с `separator`.
 */
export const AddCardsResponse = z.discriminatedUnion("status", [
  z.object({ status: z.literal("question") }),
  z.object({ status: z.literal("saved"), report: PasteReport, rev: z.number().int() }),
]);
export type AddCardsResponse = z.infer<typeof AddCardsResponse>;

/** `PATCH /v1/decks/:deckId/cards/:cardId`. Чужой `front_key` это `409 card_exists`. */
export const UpdateCardRequest = z
  .object({
    front: FlashcardSide.optional(),
    back: FlashcardSide.optional(),
    example: FlashcardExample.nullable().optional(),
  })
  .refine((b) => b.front !== undefined || b.back !== undefined || b.example !== undefined);
export type UpdateCardRequest = z.infer<typeof UpdateCardRequest>;

/**
 * `POST /v1/decks/:deckId/assignments`: «Открыть ученику» (10.3). `newPerDay`
 * необязателен: без него у обычной колоды `FLASHCARD_NEW_PER_DAY_DEFAULT`, а на
 * колоде слов сервер его игнорирует вовсе (там `NULL`, 3.3).
 */
export const AssignDeckRequest = z.object({
  requestId: z.string().uuid(),
  studentUserIds: z.array(UserId).min(1).max(100),
  newPerDay: z.number().int().min(FLASHCARD_NEW_PER_DAY_MIN).max(FLASHCARD_NEW_PER_DAY_MAX).optional(),
});
export type AssignDeckRequest = z.infer<typeof AssignDeckRequest>;

export const AssignDeckResponse = z.object({ recipients: z.array(FlashcardRecipient) });
export type AssignDeckResponse = z.infer<typeof AssignDeckResponse>;

/* ──────────────────────────── поле в сетке ─────────────────────── */

/**
 * Показывать ли поле «Слова занятия» у пары (8.3): предмет пары английский или
 * не задан. Роль смотрящего проверяет вызывающий (`wordsFieldOf`).
 */
export function wordsFieldShown(pairSubject: Subject | null): boolean {
  return pairSubject === null || pairSubject === "english";
}

/**
 * Есть ли у преподавателя английский (8.3). `null` и отсутствие списка это ВСЕ
 * предметы, правило `teacherSubjects`. Без английского сервер не читает
 * предметов пар вовсе, и поле не показывается никому.
 */
export function teachesEnglish(subjects: readonly string[] | null | undefined): boolean {
  return teacherSubjects(subjects).includes("english");
}
